import { Role } from '../../../Store/rbac-sod';

/**
 * Role-rank escalation (US #9320).
 *
 * Escalation is *derived*, not hand-wired: when a step ages out it goes to the
 * next role up the seniority ladder. This resolves "the next role up" from the
 * role catalog the page already loads — the ranked role with the smallest
 * `rank` strictly greater than the current approver's.
 *
 * Returns `null` (render as "—") when:
 *  - `approverRoleId` is null / unknown,
 *  - the owner role has no `rank` (not part of the escalation chain), or
 *  - the owner is already at the top of the chain (no higher-ranked role).
 */
export function escalatesTo(
  approverRoleId: number | null | undefined,
  roles: readonly Role[],
): Role | null {
  if (approverRoleId == null) return null;

  const owner = roles.find((r) => r.roleId === approverRoleId);
  if (!owner || owner.rank == null) return null;
  const ownerRank = owner.rank;

  let next: Role | null = null;
  for (const r of roles) {
    if (r.rank == null || r.rank <= ownerRank) continue;
    if (next === null || r.rank < (next.rank as number)) next = r;
  }
  return next;
}

/** Convenience label for templates: the escalate-to role name, or `—`. */
export function escalatesToLabel(
  approverRoleId: number | null | undefined,
  roles: readonly Role[],
): string {
  return escalatesTo(approverRoleId, roles)?.name ?? '—';
}
